// Les données de l'accueil, en un seul chargement.
// L'accueil est un passage obligé, lisible en trois secondes : il ne doit pas
// mettre trois secondes à s'afficher.

import { useCallback, useEffect, useState } from 'react'
import { addDays, todayIso } from 'sotcp-core/dayUtils'
import * as api from './api'
import { messageErreur } from '@/lib/errors'
import type { Consigne, Journee, Message, PadAttente, Run, Etape } from '@/domain/types'

const FENETRE = 12   // jours affichés dans la bande de sélection

export interface DonneesAccueil {
  aujourdhui: string
  journees: api.JourneeLegere[]
  runs: Record<string, Run>
  /** Étapes de la journée sélectionnée et de la précédente travaillée. */
  etapes: Record<string, Etape[]>
  journeeSel: Journee | null
  journeePrec: Journee | null
  datePrec: string | null
  pad: PadAttente[]
  consignes: Consigne[]
  messages: Message[]
  chargement: boolean
  erreur: string | null
  recharger: () => Promise<void>
}

export function useAccueil(dateSel: string): DonneesAccueil {
  const aujourdhui = todayIso()
  const [etat, setEtat] = useState<Omit<DonneesAccueil, 'recharger' | 'aujourdhui'>>({
    journees: [], runs: {}, etapes: {}, journeeSel: null, journeePrec: null, datePrec: null,
    pad: [], consignes: [], messages: [], chargement: true, erreur: null,
  })

  const recharger = useCallback(async () => {
    try {
      const debut = addDays(aujourdhui, -FENETRE)
      const [journees, runs, journeeSel, pad, messages] = await Promise.all([
        api.listerJournees(debut, aujourdhui),
        api.listerRuns(debut, aujourdhui),
        api.chargerJournee(dateSel),
        api.chargerPadAttente('en_attente'),
        api.chargerMessages(12),
      ])

      // « Hier » au sens métier : la dernière journée travaillée avant celle-ci,
      // pas la veille calendaire — un lundi regarde le samedi.
      const precedente = journees.find((j) => j.date < dateSel) ?? null
      const [journeePrec, etapesSel, etapesPrec, consignes] = await Promise.all([
        precedente ? api.chargerJournee(precedente.date) : Promise.resolve(null),
        api.chargerEtapes(dateSel),
        precedente ? api.chargerEtapes(precedente.date) : Promise.resolve([] as Etape[]),
        api.chargerConsignes(
          Array.from({ length: FENETRE + 1 }, (_, i) => addDays(aujourdhui, -i)),
        ),
      ])

      setEtat({
        journees,
        runs: Object.fromEntries(runs.map((r) => [r.journee_date, r])),
        etapes: { [dateSel]: etapesSel, ...(precedente ? { [precedente.date]: etapesPrec } : {}) },
        journeeSel, journeePrec, datePrec: precedente?.date ?? null,
        pad, consignes, messages, chargement: false, erreur: null,
      })
    } catch (e) {
      setEtat((s) => ({ ...s, chargement: false, erreur: messageErreur(e) }))
    }
  }, [aujourdhui, dateSel])

  useEffect(() => { void recharger() }, [recharger])

  return { ...etat, aujourdhui, recharger }
}
